import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import {
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import AddIconMU from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteIconMU from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';

import appdata from '../../Services/appdata';
import DefaultApi from '../../Api/DefaultApi';
import ConfirmDialog from '../../Components/ConfirmDialog';
import TextFieldDialog from '../../Components/TextFieldDialog';
import {
  FilterContext,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
} from '../../Components/ReactDataGridProps';
import EnhancedTableToolbar from './EnhancedTableToolbar';
import UserDialog from './UserDialog';
import { menuitems } from '../../Routes/MenuItems';
import './style.css';

function rowMenuKeyGetter(row) {
  return row.page_id;
}

function rowUserKeyGetter(row) {
  return row.id;
}

const ArrowFormatter = ({ value }) => {
  return value.includes('/') ? (
    <>
      <span style={{ position: 'absolute', top: 8 }}>
        {value.split('/')[0]}
      </span>
      <ArrowRightAltIcon style={{ marginLeft: 51, marginTop: 4 }} />
      <span style={{ position: 'absolute', top: 8 }}>
        {value.split('/')[1]}
      </span>
    </>
  ) : (
    <>{value}</>
  );
};

const menuItemTablecolumns = [
  SelectColumn,
  {
    key: 'menuItem',
    name: 'Menu Item',
    sortable: false,
    renderCell: (props) => <ArrowFormatter value={props.row.menuItem} />,
  },
];

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'email':
    case 'name':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function SecuritySettings() {
  const roleMenuItemsRef = useRef([]);
  const [openRoleNameDialog, setOpenRoleNameDialog] = useState(false);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleMenuItems, setRoleMenuItems] = useState([]);
  const [selectedMenuRows, setSelectedMenuRows] = useState(new Set());
  const [roleUsers, setRoleUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sortUserColumns, setSortUserColumns] = useState([]);
  const [filters, setFilters] = useState({
    email: '',
    name: '',
    company_org_id: 'All',
    enabled: false,
  });
  const [editingRole, setEditingRole] = useState(null);
  const [usersOfDialog, setUsersOfDialog] = useState([]);
  const [companySettings, setCompanySettings] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    deleteModal: false,
  });
  const [deleteRoleId, setDeleteRoleId] = useState(null);

  useEffect(() => {
    getComponents();
    // eslint-disable-next-line
  }, []);

  const getComponents = async () => {
    roleMenuItemsRef.current = roleMenuItems;
    menuitems.forEach((d) => parseChild(d));
    setRoleMenuItems(roleMenuItemsRef.current);
    roleMenuItemsRef.current = [];

    await Promise.all([
      DefaultApi.getRoles()
        .then((res) => {
          if (res.status === 200) {
            setRoles(res.data);
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message)),
      DefaultApi.getAllCompanySettings()
        .then((res) => {
          if (res.status === 200) {
            setCompanySettings(res.data);
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message))
    ]);    
  };

  const parseChild = (child, parent = null) => {
    let id = parent !== null ? parent + '/' + child.id : child.id;
    let page_id = child.page_id;
    let isPackage = child.isPackage;
    let sidebarName = child.sidebarName;
    let navbarName = child.navbarName;
    let children = child.children;
    if (id) {
      roleMenuItemsRef.current.push({
        menuItem: id,
        page_id: page_id,
        isPackage: isPackage,
        sidebarName: sidebarName,
        navbarName: navbarName,
      });
    }

    if (children && Array.isArray(children)) {
      children.forEach((child) => parseChild(child, id));
    }
  };

  const getSelectedRole = (role) => {
    let selected_role = roles.find((_role) => _role.id === role.id);

    let role_menu_items = [...roleMenuItems];
    let selected_menu_rows = new Set();
    role_menu_items.map((key) => {
      let selectFlag = false;
      selected_role.accesses.map((access) => {
        if (access === 'Settings' || access === 'ManagementReporting') {
          if (key.menuItem === access) selectFlag = true;
        } else {
          if (key.menuItem.includes(access)) selectFlag = true;
        }

        return access;
      });

      if (selectFlag) {
        key.isSelected = true;
        selected_menu_rows.add(key.page_id);
      } else key.isSelected = false;

      return key;
    });

    selected_role.users.map((user, idx) => {
      user.name = `${user.first_name} ${user.last_name}`;
      user.index = idx;
      return user;
    });

    setRoleMenuItems(role_menu_items);
    setRoleUsers(selected_role.users);
    setSelectedRole(selected_role);
    setSelectedMenuRows(selected_menu_rows);
  };

  const handleClickRole = (selectedRole) => {
    getSelectedRole(selectedRole);
  };

  const onCloseRoleNameDialog = async (role) => {
    setOpenRoleNameDialog(false);

    if (role) {
      if (editingRole) {
        await DefaultApi.updateRole(editingRole.id, role)
          .then(async (res) => {
            if (res.status === 200) {
              toast.success(res.message);
              await DefaultApi.getRoles()
                .then((res) => {
                  if (res.status === 200) {
                    setRoles(res.data);
                    setEditingRole(null);
                  } else toast.error(res.message);
                })
                .catch((error) => toast.error(error.message));
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
      } else {
        await DefaultApi.addRole(role)
          .then(async (res) => {
            if (res.status === 200) {
              toast.success(res.message);
              await DefaultApi.getRoles()
                .then((res) => {
                  if (res.status === 200) {
                    setRoles(res.data);
                    setEditingRole(null);
                  } else toast.warning(res.message);
                })
                .catch((error) => toast.error(error.message));
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
      }
    }
  };

  const onDeleteRole = (event, role) => {
    event.stopPropagation();

    setOpenConfirmDialog({
      open: true,
      title: 'Delete Role',
      content: 'Are you sure to delete the role?',
      deleteModal: true,
    });
    setDeleteRoleId(role.id);
  };

  const onEditRole = (event, editingRole) => {
    event.stopPropagation();
    setOpenRoleNameDialog(true);
    setEditingRole(editingRole);
  };

  const onMenuItemSelected = async (rowIndexs) => {
    let row_indexes = new Set([...rowIndexs]);
    let role_menu_items = [...roleMenuItems];
    
    role_menu_items.map((role_menu_item) => {
      if (rowIndexs.has(role_menu_item.page_id)) {
        role_menu_item.isSelected = true;
      } else {
        role_menu_item.isSelected = false;
      }
      return role_menu_item;
    });
    
    if (selectedMenuRows.size > rowIndexs.size) {
      // deselect
      if (!rowIndexs.has(3)) {
        role_menu_items.map((role_menu_item) => {
          if (role_menu_item.menuItem.includes('Settings')) {
            role_menu_item.isSelected = false;
            row_indexes.delete(role_menu_item.page_id);
          }
          return role_menu_item;
        });
      } else {
        if (
          !rowIndexs.has(31) &&
          !rowIndexs.has(32) &&
          !rowIndexs.has(33) &&
          !rowIndexs.has(34) &&
          !rowIndexs.has(35)
        ) {
          role_menu_items.map((role_menu_item) => {
            if (role_menu_item.menuItem === 'Settings') {
              row_indexes.delete(3);
              role_menu_item.isSelected = false;
            }
            return role_menu_item;
          });
        }
      }
    } else {
      // select
      if (rowIndexs.has(3)) {
        let lastElement;
        for (const item of rowIndexs) {
          lastElement = item;
        }

        if (lastElement === 3) {
          role_menu_items.map((role_menu_item) => {
            if (role_menu_item.menuItem.includes('Settings')) {
              role_menu_item.isSelected = true;
              if (!rowIndexs.has(role_menu_item.page_id)) {
                row_indexes.add(role_menu_item.page_id);
              }
            }
            return role_menu_item;
          });
        }        
      } else {
        if (
          rowIndexs.has(31) &&
          rowIndexs.has(32) &&
          rowIndexs.has(33) &&
          rowIndexs.has(34) &&
          rowIndexs.has(35)
        ) {
          role_menu_items.map((role_menu_item) => {
            if (role_menu_item.menuItem === 'Settings') {
              row_indexes.add(3);
              role_menu_item.isSelected = true;
            }
            return role_menu_item;
          });
        }
      }
    }

    setSelectedMenuRows(row_indexes);
    setRoleMenuItems(role_menu_items);

    let roleAccesses = [];
    role_menu_items.forEach((menuItem) => {
      if (menuItem.isSelected) {
        if (menuItem.menuItem.includes('/')) {
          if (!roleAccesses.includes(menuItem.menuItem.split('/')[0])) {
            roleAccesses.push(menuItem.menuItem.split('/')[0]);
            role_menu_items.find(
              (item) => item.menuItem === menuItem.menuItem.split('/')[0],
            ).isSelected = true;
          }
          roleAccesses.push(menuItem.menuItem.split('/')[1]);
        } else {
          roleAccesses.push(menuItem.menuItem);
        }
      }
    });

    appdata.setItem('changedrole', {
      id: selectedRole.id,
      pages: roleAccesses,
    });
    await DefaultApi.updateRole(selectedRole.id, null, roleAccesses)
      .then(async (res) => {
        if (res.status === 200) {
          await DefaultApi.getRoles()
            .then((res) => {
              if (res.status === 200) {
                setRoles(res.data);
              } else toast.error(res.message);
            })
            .catch((error) => toast.error(error.message));
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const onUserSelected = (ids) => {
    setSelectedUserIds(ids);
    let role_users = [...roleUsers];
    let selected_users = [];
    ids.forEach((id) => {
      role_users.filter((user) =>
        user.id === id ? (user.isSelected = true) : (user.isSelected = false),
      );
    });

    for (const user of role_users) {
      if (user.isSelected) selected_users.push(user);
    }

    setRoleUsers(role_users);
    setSelectedUsers(selected_users);
  };

  const handleGridUserRowsUpdated = async (updatedRows, field) => {
    setRoleUsers(updatedRows);
    await DefaultApi.updateCompanyOrgId(
      updatedRows[field.indexes[0]].id,
      updatedRows[field.indexes[0]][field.column.key],
    )
      .then((res) => {
        if (res.status === 200) {
          let company = appdata.getItem('company');
          company.company_org_id =
            updatedRows[field.indexes[0]][field.column.key];
          appdata.setItem('company', company);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const onAddUser = () => {
    let users_OfDialog = [];
    for (const role of roles) {
      if (role.id === selectedRole.id) continue;
      for (const user of role.users) {
        user.name = `${user.first_name} ${user.last_name}`;
        users_OfDialog.push(user);
      }
    }
    setOpenAddUserDialog(true);
    setUsersOfDialog(users_OfDialog);
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const onDeleteUser = async () => {
    setOpenConfirmDialog({
      open: true,
      title: 'Delete User',
      content: 'Are you sure to delete the users from the role?',
      deleteModal: true,
    });
  };

  const onCloseUsersDialog = async (selectedUsers) => {
    setOpenAddUserDialog(false);
    if (selectedUsers && selectedUsers.length > 0) {
      await DefaultApi.addUsersToRole(selectedRole.id, selectedUsers)
        .then(async (res) => {
          if (res.status === 200) {
            await DefaultApi.getRoles()
              .then((res) => {
                if (res.status === 200) setRoles(res.data);
                else toast.error(res.message);
              })
              .catch((error) => toast.error(error.message));
            getSelectedRole(selectedRole);
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    }
  };

  const onCloseInviteDialog = async (email) => {
    if (email) {
      await DefaultApi.invite(email, selectedRole.id, 0)
        .then(async (res) => {
          if (res.status === 200) {
            setOpenInviteDialog(false);
            toast.success(res.message);
          } else {
            toast.error(res.message);
            setOpenConfirmDialog({
              open: true,
              title: 'Invite',
              content:
                'If the user creates a new user on a paid account they must accept that the next charge will include a higher amount for each new user.',
              deleteModal: false,
            });
          }
        })
        .catch((error) => toast.error(error.message));
    }
  };

  const handleConfirmDialog = async () => {
    if (openConfirmDialog.title === 'Invite') {
      setOpenConfirmDialog({ ...openConfirmDialog, open: false });
      setOpenInviteDialog(false);
      await DefaultApi.invite(email, selectedRole.id, 1)
        .then((res) => toast.success(res.message))
        .catch((error) => toast.error(error.message));
    } else if (openConfirmDialog.title === 'Delete Role') {
      setOpenConfirmDialog({ ...openConfirmDialog, open: false });
      await DefaultApi.deleteRole(deleteRoleId)
        .then(async (res) => {
          if (res.status === 200) {
            toast.success(res.message);
            setDeleteRoleId(null);
            await DefaultApi.getRoles()
              .then((res) => {
                if (res.status === 200) {
                  setRoles(res.data);
                } else toast.error(res.message);
              })
              .catch((error) => toast.error(error.message));
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    } else if (openConfirmDialog.title === 'Delete User') {
      setOpenConfirmDialog({ ...openConfirmDialog, open: false });
      if (selectedUsers.length > 0) {
        await DefaultApi.deleteUsersFromRole(selectedRole.id, selectedUsers)
          .then(async (res) => {
            if (res.status === 200) {
              toast.success(res.message);
              await DefaultApi.getRoles()
                .then((res) => {
                  if (res.status === 200) {
                    setRoles(res.data);
                    setSelectedUsers([], () => {
                      getSelectedRole(selectedRole);
                    });
                  } else toast.error(res.message);
                })
                .catch((error) => toast.error(error.message));
            } else {
              toast.error(res.message);
              window.location.reload();
            }
          })
          .catch((error) => toast.error(error.message));
      }
    }
  };

  const userTablecolumns = useMemo(() => {
    return [
      SelectColumn,
      {
        key: 'email',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>User Email</b>
            {sortUserColumns.find((column) => column.columnKey === 'email')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortUserColumns.find((column) => column.columnKey === 'email')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.email}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    email: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'name',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>User Name</b>
            {sortUserColumns.find((column) => column.columnKey === 'name')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortUserColumns.find((column) => column.columnKey === 'name')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'company_org_id',
        name: 'Company Name',
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.company_org_id}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, company_org_id: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companySettings.map((cur, index) => (
                  cur.IsActive && (
                    <option key={index} value={cur.id}>
                      {cur.CompanyName}
                    </option>
                  )
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.company_org_id ? props.row.company_org_id : 0}
            onChange={(e) =>
              props.onRowChange(
                {
                  ...props.row,
                  company_org_id: e.target.value,
                },
                true,
              )
            }
          >
            <MenuItem value={0}>NONE</MenuItem>
            {companySettings.map((comp, idx) => {
              if (comp.IsActive === true)
                return (
                  <MenuItem key={idx} value={comp.id}>
                    {comp.CompanyName}
                  </MenuItem>
                );
              else return null;
            })}
          </Select>
        ),
      },
    ];
  }, [companySettings, sortUserColumns]);

  const filteredRows = useMemo(() => {
    return roleUsers?.filter((r) => {
      return (
        (filters.name
          ? r.name?.toLowerCase().includes(filters.name.toLowerCase())
          : true) &&
        (filters.email
          ? r.email?.toLowerCase().includes(filters.email.toLowerCase())
          : true) &&
        (filters.company_org_id !== 'All'
          ? r.company_org_id === filters.company_org_id
          : true)
      );
    });
  }, [roleUsers, filters]);

  const sortUserRows = useMemo(() => {
    if (sortUserColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortUserColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortUserColumns]);
  
  return (
    <div className="SecuritySettings">
      <Paper style={{ marginRight: 8 }}>
        <Table style={{ minWidth: 160 }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontSize: 20 }}>
                <TableSortLabel direction={'asc'}>Roles</TableSortLabel>
                <div className="role-button-bar">
                  <IconButton
                    aria-label="Add"
                    onClick={() => {
                      setOpenRoleNameDialog(true);
                    }}
                    sx={{ padding: 0 }}
                  >
                    <AddIconMU />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role, index) => (
              <TableRow
                key={index.toString()}
                hover
                selected={selectedRole && selectedRole.id === role.id}
                onClick={() => handleClickRole(role)}
              >
                <TableCell
                  style={{ fontSize: 16, paddingRight: 8 }}
                  className="role-table-cell"
                >
                  {role.name}
                  <div className="role-button-bar">
                    <IconButton
                      aria-label="Delete"
                      onClick={(event) => onDeleteRole(event, role)}
                      sx={{ padding: 0 }}
                    >
                      <DeleteIconMU />
                    </IconButton>
                    <IconButton
                      aria-label="Edit"
                      onClick={(event) => onEditRole(event, role)}
                      sx={{ padding: 0 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {selectedRole && (
        <Grid
          container
          spacing={1}
          sx={{ width: '100%' }}
          justifyContent="center"
        >
          <Grid size={{ sm: 12, md: 4 }}>
            <Paper className="grid-paper">
              <DataGrid
                className="rdg-light"
                rowKeyGetter={rowMenuKeyGetter}
                columns={menuItemTablecolumns}
                rows={roleMenuItems}
                rowHeight={35}
                selectedRows={selectedMenuRows}
                onSelectedRowsChange={onMenuItemSelected}
                style={{ height: window.innerHeight - 108 }}
              />
            </Paper>
          </Grid>
          <Grid size={{ sm: 12, md: 8 }}>
            <Paper className="grid-paper-sec">
              <EnhancedTableToolbar
                numSelected={selectedUsers.length}
                onAddUser={onAddUser}
                toggleFilters={toggleFilters}
                onDeleteUser={onDeleteUser}
                onInvite={() => {
                  setOpenInviteDialog(true);
                }}
              />
              <FilterContext.Provider value={filters}>
                <DataGrid
                  rowKeyGetter={rowUserKeyGetter}
                  className={
                    filters.enabled
                      ? 'filter-container fill-grid rdg-light'
                      : 'fill-grid rdg-light'
                  }
                  headerRowHeight={filters.enabled ? 70 : 50}
                  columns={userTablecolumns}
                  rows={sortUserRows}
                  sortColumns={sortUserColumns}
                  onSortColumnsChange={setSortUserColumns}
                  rowHeight={50}
                  selectedRows={selectedUserIds}
                  onSelectedRowsChange={onUserSelected}
                  onRowsChange={handleGridUserRowsUpdated}
                  style={{ height: window.innerHeight - 170 }}
                />
              </FilterContext.Provider>
            </Paper>
          </Grid>
        </Grid>
      )}

      <TextFieldDialog
        open={openRoleNameDialog}
        title={editingRole ? 'Edit Role' : 'Add Role'}
        content={`To ${
          editingRole ? 'edit role' : 'add role'
        }, please enter role name`}
        label="Role Name"
        onCancel={() => {
          setOpenRoleNameDialog(false);
        }}
        onOK={onCloseRoleNameDialog}
      />

      <TextFieldDialog
        open={openInviteDialog}
        title="Invite"
        content="To invite, please enter email"
        label="Email"
        onCancel={() => {
          setOpenInviteDialog(false);
        }}
        onOK={onCloseInviteDialog}
      />

      <UserDialog
        open={openAddUserDialog}
        users={usersOfDialog}
        onClose={onCloseUsersDialog}
      />

      <ConfirmDialog
        open={openConfirmDialog.open}
        title={openConfirmDialog.title}
        content={openConfirmDialog.content}
        deleteModal={openConfirmDialog.deleteModal}
        onClose={() => {
          setOpenConfirmDialog({ ...openConfirmDialog, open: false });
          setDeleteRoleId(null);
        }}
        onConfirm={handleConfirmDialog}
      />
    </div>
  );
}

export default SecuritySettings;
